/**
 * Global Video Call Service
 * Handles video calls globally across the entire application
 * This service ensures users can receive video calls from any part of the app
 */

import { reactive } from 'vue';
import useAlert from '@/composables/useAlert';

class GlobalVideoCallService {
  constructor() {
    // Global video call state
    this.state = reactive({
      // Incoming call state
      incomingCall: {
        visible: false,
        data: null,
        caller: null,
        type: 'video', // 'video' or 'voice'
        roomId: null,
        expiresAt: null,
        callId: null
      },
      
      // Active call state
      activeCall: {
        active: false,
        minimized: false,
        type: 'video',
        roomId: null,
        roomUrl: null,
        startTime: null,
        callId: null,
        userRole: 'participant'
      }
    });

    // Event listeners for global video call events
    this.eventListeners = new Map();
    
    // Service initialization flag
    this.initialized = false;
    
    // Bind methods to preserve context
    this.handleIncomingCall = this.handleIncomingCall.bind(this);
    this.handleCallAccepted = this.handleCallAccepted.bind(this);
    this.handleCallDeclined = this.handleCallDeclined.bind(this);
    this.handleCallExpired = this.handleCallExpired.bind(this);
  }

  /**
   * Initialize the global video call service
   * This should be called once when the app starts
   */
  initialize() {
    if (this.initialized) {
      console.log('GlobalVideoCallService already initialized');
      return;
    }

    console.log('🎥 INITIALIZING GLOBAL VIDEO CALL SERVICE');
    
    // Set up global event listeners
    this.setupGlobalEventListeners();
    
    this.initialized = true;
    console.log('✅ GLOBAL VIDEO CALL SERVICE INITIALIZED');
  }

  /**
   * Set up global event listeners for video call events
   */
  setupGlobalEventListeners() {
    // Import and connect to the realtime messaging service
    try {
      const realtimeMessagingService = require('@/services/realtime-messaging').default;
      
      console.log('🔗 CONNECTING TO REALTIME MESSAGING SERVICE:', realtimeMessagingService);
      
      // Listen for video call request events from the realtime messaging service
      realtimeMessagingService.on('video-call-request', (event) => {
        console.log('📞 VIDEO CALL REQUEST RECEIVED IN GLOBAL SERVICE:', event);
        this.handleIncomingCall(event);
      });
      
      realtimeMessagingService.on('voice-call-request', (event) => {
        console.log('📞 VOICE CALL REQUEST RECEIVED IN GLOBAL SERVICE:', event);
        this.handleIncomingCall(event);
      });
      
      console.log('🎧 GLOBAL VIDEO CALL EVENT LISTENERS SETUP COMPLETE');
    } catch (error) {
      console.error('❌ ERROR SETTING UP GLOBAL EVENT LISTENERS:', error);
    }
  }

  /**
   * Handle incoming call from anywhere in the app
   * @param {Object} event - The incoming call event
   */
  handleIncomingCall(event) {
    const { sender, metadata, message_type } = event;
    
    // Don't show incoming call for own calls
    const currentUser = this.getCurrentUser();
    if (sender.id === currentUser?.id) {
      return;
    }
    
    const callType = message_type === 'video_call_request' ? 'video' : 'voice';
    
    // Log comprehensive call information
    console.log('📞 GLOBAL INCOMING CALL RECEIVED:', {
      timestamp: new Date().toISOString(),
      callType: callType,
      messageType: message_type,
      sender: {
        id: sender.id,
        name: sender.name,
        email: sender.email,
        profile_image: sender.profile_image
      },
      metadata: {
        room_id: metadata?.room_id,
        call_id: metadata?.call_id,
        expires_at: metadata?.expires_at,
        request_id: metadata?.request_id,
        call_type: metadata?.call_type
      },
      currentUser: {
        id: currentUser?.id,
        name: currentUser?.name
      }
    });
    
    // Update global incoming call state
    this.state.incomingCall = {
      visible: true,
      data: event,
      caller: sender,
      type: callType,
      roomId: metadata?.room_id,
      expiresAt: metadata?.expires_at,
      callId: metadata?.call_id || null
    };
    
    console.log('📞 UPDATED GLOBAL INCOMING CALL STATE:', this.state.incomingCall);
    console.log('👤 CALLER DATA:', this.state.incomingCall.caller);
    
    // Emit global event for UI components to react
    this.emit('incoming-call-received', this.state.incomingCall);
  }

  /**
   * Handle call accepted from the global incoming call modal
   * @param {Object} callData - The call data from the modal
   */
  async handleCallAccepted(callData) {
    console.log('✅ GLOBAL CALL ACCEPTED HANDLED:', {
      timestamp: new Date().toISOString(),
      callData: callData,
      currentVideoCallState: this.state.activeCall
    });
    
    try {
      // Start the video call interface
      const newVideoCallState = {
        active: true,
        minimized: false,
        type: callData.callType,
        roomId: callData.roomId,
        roomUrl: callData.roomUrl,
        startTime: new Date(),
        callId: callData.callId,
        userRole: callData.userRole || 'participant'
      };
      
      console.log('🎥 SETTING GLOBAL VIDEO CALL STATE:', {
        timestamp: new Date().toISOString(),
        oldState: this.state.activeCall,
        newState: newVideoCallState
      });
      
      this.state.activeCall = newVideoCallState;
      
      // Ensure the incoming call modal is hidden
      this.state.incomingCall.visible = false;
      
      // Emit global event for UI components to react
      this.emit('call-started', this.state.activeCall);
      
      console.log('✅ GLOBAL CALL ACCEPTANCE HANDLING COMPLETED:', {
        timestamp: new Date().toISOString(),
        videoCallActive: this.state.activeCall.active,
        roomId: this.state.activeCall.roomId,
        callId: this.state.activeCall.callId
      });
      
    } catch (error) {
      console.error('❌ ERROR IN GLOBAL CALL ACCEPTANCE:', error);
      this.handleCallError(error);
    }
  }

  /**
   * Handle call declined from the global incoming call modal
   * @param {Object} callData - The call data from the modal
   */
  handleCallDeclined(callData) {
    console.log('❌ GLOBAL CALL DECLINED HANDLED:', {
      timestamp: new Date().toISOString(),
      callData: callData
    });
    
    // Hide the incoming call modal
    this.state.incomingCall.visible = false;
    
    // Emit global event for UI components to react
    this.emit('call-declined', callData);
  }

  /**
   * Handle call expired from the global incoming call modal
   * @param {Object} callData - The call data from the modal
   */
  handleCallExpired(callData) {
    console.log('⏰ GLOBAL CALL EXPIRED HANDLED:', {
      timestamp: new Date().toISOString(),
      callData: callData
    });
    
    // Hide the incoming call modal
    this.state.incomingCall.visible = false;
    
    // Emit global event for UI components to react
    this.emit('call-expired', callData);
  }

  /**
   * Handle call errors
   * @param {Error} error - The error that occurred
   */
  handleCallError(error) {
    console.error('❌ GLOBAL CALL ERROR HANDLED:', {
      timestamp: new Date().toISOString(),
      error: error
    });
    
    try {
      useAlert(error.message || 'Call error occurred', 'danger');
    } catch (alertError) {
      console.error('Alert error:', alertError);
      console.log('Call error:', error.message || 'Call error occurred');
    }
  }

  /**
   * End the active call
   */
  endActiveCall() {
    if (!this.state.activeCall.active) {
      return;
    }
    
    console.log('🔚 ENDING GLOBAL ACTIVE CALL:', {
      timestamp: new Date().toISOString(),
      callState: this.state.activeCall
    });
    
    // Reset active call state
    this.state.activeCall = {
      active: false,
      minimized: false,
      type: 'video',
      roomId: null,
      roomUrl: null,
      startTime: null,
      callId: null,
      userRole: 'participant'
    };
    
    // Emit global event for UI components to react
    this.emit('call-ended', {});
  }

  /**
   * Minimize the active call
   */
  minimizeCall() {
    if (this.state.activeCall.active) {
      this.state.activeCall.minimized = true;
      this.emit('call-minimized', this.state.activeCall);
    }
  }

  /**
   * Restore the minimized call
   */
  restoreCall() {
    if (this.state.activeCall.active && this.state.activeCall.minimized) {
      this.state.activeCall.minimized = false;
      this.emit('call-restored', this.state.activeCall);
    }
  }

  /**
   * Get current user from auth store
   * @returns {Object|null} Current user object
   */
  getCurrentUser() {
    try {
      // Try to get user from Pinia store
      const { useAuthStore } = require('@/stores');
      const authStore = useAuthStore();
      return authStore.user;
    } catch (error) {
      console.warn('Could not get current user from auth store:', error);
      return null;
    }
  }

  /**
   * Add event listener
   * @param {string} eventType - The event type to listen for
   * @param {Function} handler - The handler function
   */
  on(eventType, handler) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType).push(handler);
  }

  /**
   * Remove event listener
   * @param {string} eventType - The event type
   * @param {Function} handler - The handler function to remove
   */
  off(eventType, handler) {
    const handlers = this.eventListeners.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to registered handlers
   * @param {string} eventType - The event type
   * @param {Object} data - The event data
   */
  emit(eventType, data) {
    const handlers = this.eventListeners.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${eventType} handler:`, error);
        }
      });
    }
  }

  /**
   * Clean up the service
   */
  cleanup() {
    console.log('🧹 CLEANING UP GLOBAL VIDEO CALL SERVICE');
    
    // Clear event listeners
    this.eventListeners.clear();
    
    // Reset state
    this.state.incomingCall = {
      visible: false,
      data: null,
      caller: null,
      type: 'video',
      roomId: null,
      expiresAt: null,
      callId: null
    };
    
    this.state.activeCall = {
      active: false,
      minimized: false,
      type: 'video',
      roomId: null,
      roomUrl: null,
      startTime: null,
      callId: null,
      userRole: 'participant'
    };
    
    this.initialized = false;
    console.log('✅ GLOBAL VIDEO CALL SERVICE CLEANED UP');
  }
}

// Create and export a singleton instance
const globalVideoCallService = new GlobalVideoCallService();

export default globalVideoCallService;
